import extractUrls from "extract-urls";
import DOMPurify from "dompurify";

/**
 * What a support message body is allowed to render.
 *
 * A body is text somebody else wrote. Anything in it that FETCHES a resource turns
 * reading a message into a request to whoever wrote it — they learn when it was read,
 * from which IP and browser — and a same-site state-changing GET becomes a stored CSRF
 * target. Anything that RUNS is worse. Attachments render separately from their own
 * validated URLs, so nothing in a body needs to load anything.
 *
 * An ALLOW-list, because a deny-list could not win this: naming the resource tags left
 * `<table background="https://...">` untouched — obsolete, still fetched by browsers —
 * and there is a long tail of those. What a body legitimately contains is short and
 * known: the linkifier's anchors, the `<b>` a mention is wrapped in, and basic
 * formatting.
 *
 * Shared by the customer widget and the agent console on purpose. The two must render a
 * message identically, or an agent is answering something the customer cannot see; a
 * single definition is the only way to keep that true as either surface changes.
 */

// `style` is deliberately absent from the attributes as well as forbidden: an inline
// `background-image` is another way to make the reader fetch something. Markup that
// renders as nothing (`hidden`, `<template>`) is absent for a different reason — the
// server rewrites the vendor's name out of every non-customer message, and it can only
// match what it can see: `Vi<span hidden>x</span>va` reads as "Vixva" to a matcher and
// as "Viva" on screen.
export const BODY_TAGS = [
    "a", "b", "i", "em", "strong", "u", "s", "br",
    "p", "div", "span", "ul", "ol", "li", "blockquote", "pre", "code",
];

// `target` and `rel` are NOT here, and that is the point: the navigation policy of a
// link is not the sender's to choose. Every surviving anchor gets the safe pair stamped
// on it below.
export const BODY_ATTRS = ["href"];

// The widget's linkifier marks off-site anchors so the container can draw the outbound
// icon. Inert — a CSS hook, nothing more.
export const WIDGET_BODY_ATTRS = [...BODY_ATTRS, "data-external-link"];

// Assistant text is not linkified at all: only the `links` the server validated against
// this user's navigation map are clickable, so a hallucinated or prompt-injected URL in
// the prose cannot become an anchor. That leaves plain formatting.
export const ASSISTANT_BODY_TAGS = ["b", "i", "em", "strong", "br", "p", "ul", "ol", "li"];

// No custom ALLOWED_URI_REGEXP anywhere below. DOMPurify applies that pattern to every
// allowed attribute it does not already classify as URI-safe, so `^(?:https?:|mailto:)`
// silently ate `target="_blank"` and `rel="noopener noreferrer"` off the linkifiers' own
// anchors — an external link then replaced the app tab, taking an in-progress draft and
// any staged attachments with it. DOMPurify's default already refuses `javascript:` and
// `data:`, which is the part that matters; the tag and attribute lists do the rest.
//
// `data-*` and `aria-*` are allowed by DOMPurify's defaults and are turned OFF here, so
// the lists above are the whole truth. Neither is dangerous, but `data-external-link` is
// a CSS hook the widget owns, and a body is not the place to hand it out or to relabel
// what a screen reader announces.
const BASE = { ALLOW_DATA_ATTR: false, ALLOW_ARIA_ATTR: false };

/**
 * Give every surviving anchor the same navigation policy, whoever wrote it.
 *
 * The linkifiers shelve markup that is already in the body and restore it untouched, so
 * an `<a>` can reach the sanitizer straight from the sender — an inbound email reply is
 * HTML, and pulse stores what it is sent. Letting that anchor keep its own `target` and
 * `rel` hands the sender two things they should not have: `target="_self"` replaces the
 * app tab, which loses an in-progress draft and any staged attachments, and
 * `rel="opener"` deliberately gives the opened page a handle on `window.opener`.
 *
 * So the sanitizer refuses both attributes and they are stamped back on here. Every link
 * in a support message opens in a new tab, severed, and no body can say otherwise.
 *
 * A `RETURN_DOM_FRAGMENT` pass rather than a DOMPurify hook: hooks are global to the
 * module, and `Inbox/Messages.js` shares this DOMPurify — a hook registered for support
 * bodies would silently rewrite team-chat messages too.
 */
const withSafeLinkPolicy = (fragment) => {
    fragment.querySelectorAll("a").forEach((anchor) => {
        // `hasAttribute`, not a truthiness test on the value. An anchor the sanitizer
        // stripped the href from is not a link any more and gets nothing — but DOMPurify
        // KEEPS `href=""`, which reads as empty and is not: clicking it navigates to the
        // current URL, reloading the app and taking the customer's draft and staged files
        // with it. That is exactly what this policy exists to stop, so it counts as a
        // link and gets sent to a new tab like every other one.
        if (!anchor.hasAttribute("href")) return;
        anchor.setAttribute("target", "_blank");
        anchor.setAttribute("rel", "noopener noreferrer");
    });
    const holder = fragment.ownerDocument.createElement("div");
    holder.appendChild(fragment);
    return holder.innerHTML;
};

/** Human-written text, already run through the caller's linkifier. */
export const sanitizeBody = (html, { attrs = BODY_ATTRS } = {}) =>
    withSafeLinkPolicy(DOMPurify.sanitize(String(html || ""), {
        ...BASE,
        ALLOWED_TAGS: BODY_TAGS,
        ALLOWED_ATTR: attrs,
        RETURN_DOM_FRAGMENT: true,
    }));

/** Assistant prose. No anchors, no attributes. */
export const sanitizeAssistantBody = (html) =>
    DOMPurify.sanitize(String(html || ""), { ...BASE, ALLOWED_TAGS: ASSISTANT_BODY_TAGS, ALLOWED_ATTR: [] });

/**
 * Turn bare URLs in a message body into links.
 *
 * Both surfaces had their own copy of this. They agreed on all twenty differential cases
 * except one attribute — the widget marks the links it generated — which is exactly the
 * kind of near-identical pair that drifts into disagreeing about something that matters.
 * One implementation, one parameter.
 *
 * Existing markup is shelved before anything is matched: anchors first, so their contents
 * are not re-linkified, then every remaining tag, so a URL sitting in an attribute is
 * never mistaken for text. Longest URLs are substituted first, so one that is a prefix of
 * another cannot eat it. The result still goes through `sanitizeBody` — this only decides
 * what is a link, never what is safe.
 */
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Text made safe to hand to an HTML parser, with every character it contains preserved.
 *
 * Every message body in this system is PLAIN TEXT. The widget composes in a textarea,
 * the console sends `innerText`, pulse stores the mail's text part, the Intercom import
 * strips every tag and decodes the entities behind it, and the assistant is told to write
 * plain prose. Nothing authors HTML.
 *
 * Rendering them as HTML anyway meant the reader lost whatever looked like a tag.
 * `The page emitted <img src="/broken.png">` displayed as `The page emitted `, and
 * `Error in <MyComponent /> at line 4` as `Error in  at line 4` — in the support channel
 * of a software product, where people paste component names, markup and stack traces all
 * day. The sanitizer was doing its job; it was being asked the wrong question.
 *
 * Escaping asks the right one, and is strictly safer than sanitizing: after this, the only
 * markup in the string is the anchors we generate ourselves.
 */
export const escapeHtml = (text) => String(text ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

export const hyperlinkUrls = (text, { anchorAttrs = "", escaped = false } = {}) => {
  if (!text) return "";
  // Not when the caller escaped the text itself: those `&amp;` are an author's literal
  // ampersands, and decoding them here would undo exactly the escaping that keeps their
  // words intact.
  const decoded = escaped ? text : text.replace(/&amp;/g, "&");

  // The placeholders have to be text the body cannot already contain, because they are
  // put back with a global replace. Fixed markers were guessable: a message whose own
  // text read `__SUPPORT_URL_0__` had that literal turned into whichever link the first
  // placeholder stood for, so the transcript displayed something the sender never wrote.
  // A per-call nonce cannot be predicted by someone typing a message.
  const nonce = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

  const markup = new Map();
  let markupIdx = 0;
  const shelve = (m) => {
    const key = ` __SUPPORT_MARKUP_${nonce}_${markupIdx++}__ `;
    markup.set(key, m);
    return key;
  };

  let out = decoded.replace(/<a\b[^>]*>[\s\S]*?<\/a>/gi, shelve);
  out = out.replace(/<[^>]+>/g, shelve);

  const urls = Array.from(new Set(extractUrls(out) || [])).sort((a, b) => b.length - a.length);
  const links = new Map();
  urls.forEach((url, i) => {
    const key = `__SUPPORT_URL_${nonce}_${i}__`;
    const normalized = url.startsWith("http") ? url : `https://${url}`;
    links.set(
      key,
      `<a href="${normalized}"${anchorAttrs} target="_blank" rel="noopener noreferrer">${url}</a>`
    );
    const rawEsc = escapeRe(url);
    out = out.replace(new RegExp(`${rawEsc}\\.(?=\\s|$)`, "g"), `${key}.`);
    out = out.replace(new RegExp(`${rawEsc}(?!\\.)`, "g"), key);
  });

  links.forEach((anchor, key) => { out = out.replaceAll(key, anchor); });
  markup.forEach((tag, key) => { out = out.replaceAll(key, tag); });
  return out;
};

/**
 * A stored message body, rendered the way it was written.
 *
 * ONE place, because both surfaces render the same plain text and a rule that lives at
 * each call site is a rule that will hold at one of them. Escape first so the author's
 * own angle brackets survive, then linkify, then sanitize — which by then can only be
 * looking at anchors this module generated.
 */
export const renderMessageBody = (text, { anchorAttrs = "", attrs = BODY_ATTRS } = {}) => {
  const src = String(text ?? "");
  if (!src) return "";

  // URLs are taken out of the RAW text, before anything is escaped. Escaping first turns
  // the `&` of a query string into `&amp;`, and URL extraction then stops at the `;` —
  // `?a=1&b=2` became a link to `?a=1&amp;` with `;b=2` left behind as text. So the URLs
  // are lifted out while they are still themselves, and what remains is escaped around
  // them.
  //
  // The placeholder is word characters only, so escaping cannot touch it, and it carries
  // a per-call nonce for the reason the linkifier has always had one: a fixed marker is
  // guessable, and a message whose own text read it had that literal replaced by somebody
  // else's link.
  const nonce = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  const urls = Array.from(new Set(extractUrls(src) || [])).sort((a, b) => b.length - a.length);

  const anchors = new Map();
  let out = src;
  urls.forEach((url, i) => {
    const key = `__SUPPORT_URL_${nonce}_${i}__`;
    const normalized = url.startsWith("http") ? url : `https://${url}`;
    anchors.set(
      key,
      `<a href="${escapeHtml(normalized)}"${anchorAttrs} target="_blank" rel="noopener noreferrer">${escapeHtml(url)}</a>`
    );
    const rawEsc = escapeRe(url);
    // A sentence-ending full stop belongs to the sentence, not to the link.
    out = out.replace(new RegExp(`${rawEsc}\\.(?=\\s|$)`, "g"), `${key}.`);
    out = out.replace(new RegExp(`${rawEsc}(?!\\.)`, "g"), key);
  });

  out = escapeHtml(out);
  anchors.forEach((anchor, key) => { out = out.replaceAll(key, anchor); });
  return sanitizeBody(out, { attrs });
};

/** The same, for assistant prose: escaped, and with no anchors of any kind. */
export const renderAssistantBody = (text) => sanitizeAssistantBody(escapeHtml(text));
